import React, { useState, useMemo } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import './TimelineView.css';

const TimelineView = ({ placements, filters }) => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Process placements into timeline events
  const timelineEvents = useMemo(() => {
    const events = [];

    placements.forEach(placement => {
      // Application event
      if (placement.application_date) {
        events.push({
          id: `${placement.id}-application`,
          placementId: placement.id,
          company: placement.company_name,
          type: 'application',
          date: placement.application_date,
          title: 'Application Submitted',
          description: `Applied for ${placement.position_role}`,
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }

      // Online Assessment event
      if (placement.online_assessment_date) {
        events.push({
          id: `${placement.id}-oa`,
          placementId: placement.id,
          company: placement.company_name,
          type: 'assessment',
          date: placement.online_assessment_date,
          title: 'Online Assessment',
          description: 'Completed online assessment',
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }

      // Interview events
      if (placement.interview_dates && placement.interview_dates.length > 0) {
        placement.interview_dates.forEach((date, index) => {
          events.push({
            id: `${placement.id}-interview-${index}`,
            placementId: placement.id,
            company: placement.company_name,
            type: 'interview',
            date: date,
            title: `Interview ${index + 1}`,
            description: `Interview round ${index + 1}`,
            status: placement.application_status,
            priority: placement.priority_rating
          });
        });
      }

      // Result event
      if (placement.result_date) {
        events.push({
          id: `${placement.id}-result`,
          placementId: placement.id,
          company: placement.company_name,
          type: 'result',
          date: placement.result_date,
          title: 'Result',
          description: `Status: ${placement.application_status}`,
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }
    });

    // Filter events
    let filteredEvents = events;

    if (selectedCompany) {
      filteredEvents = filteredEvents.filter(event =>
        event.company.toLowerCase().includes(selectedCompany.toLowerCase())
      );
    }

    if (selectedStatus) {
      filteredEvents = filteredEvents.filter(event => event.status === selectedStatus);
    }

    // Sort by date
    return filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [placements, selectedCompany, selectedStatus]);

  const getEventIcon = (type) => {
    switch (type) {
      case 'application': return 'fa-paper-plane';
      case 'assessment': return 'fa-laptop-code';
      case 'interview': return 'fa-users';
      case 'result': return 'fa-flag-checkered';
      default: return 'fa-circle';
    }
  };

  const getEventColor = (status) => {
    switch (status) {
      case 'Applied': return '#007bff';
      case 'Rejected': return '#dc3545';
      case 'PPO Rejected': return '#fd7e14';
      case 'Session': return '#6f42c1';
      case 'Interview Scheduled': return '#ffc107';
      case 'Offer Received': return '#28a745';
      case 'Accepted': return '#20c997';
      case 'Declined': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  };

  const uniqueCompanies = [...new Set(placements.map(p => p.company_name))].sort();
  const uniqueStatuses = [...new Set(placements.map(p => p.application_status))].sort();

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <h3>Application Timeline</h3>
        <p>Visual timeline of your application progression</p>
      </div>

      {/* Filters */}
      <div className="timeline-filters">
        <div className="filter-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search companies..."
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <select
            className="form-control"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="filter-stats">
          <span className="event-count">{timelineEvents.length} events</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="timeline-content">
        {timelineEvents.length === 0 ? (
          <div className="no-events">
            <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
            <h4>No Events Found</h4>
            <p className="text-muted">Try adjusting your filters or add more placement data</p>
          </div>
        ) : (
          <div className="timeline">
            {timelineEvents.map((event, index) => (
              <div key={event.id} className="timeline-item">
                <div className="timeline-marker">
                  <div
                    className="timeline-icon"
                    style={{ backgroundColor: getEventColor(event.status) }}
                  >
                    <i className={`fas ${getEventIcon(event.type)}`}></i>
                  </div>
                </div>

                <div className="timeline-content-item">
                  <div className="timeline-card">
                    <div className="timeline-card-header">
                      <h5 className="timeline-title">{event.title}</h5>
                      <span className="timeline-date">{formatDate(event.date)}</span>
                    </div>

                    <div className="timeline-card-body">
                      <div className="company-name">{event.company}</div>
                      <div className="event-description">{event.description}</div>

                      <div className="event-meta">
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getEventColor(event.status) }}
                        >
                          {event.status}
                        </span>

                        {event.priority && (
                          <span className="priority-rating">
                            {'★'.repeat(event.priority)}{'☆'.repeat(5 - event.priority)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineView;
